import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Trash2,
  Plus,
  Minus,
  ArrowLeft,
  ShoppingBag,
  Package,
  AlertCircle,
  CreditCard,
  Shield,
  Truck,
} from "lucide-react";
import { useCart } from "../../hooks/useCart";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import LightLoader from "../common/LightLoader";
import { SkeletonCartItem } from "../common/SkeletonLoader";
import ImageWithFallback from "../common/ImageWithFallback";
import LoadingSpinner from "../common/LoadingSpinner";
import { API_BASE_URL, validateCoupon } from "../../config/api";

const CartPage = () => {
  const {
    cart,
    loading,
    error: cartError,
    refetch: refetchCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    couponCode,
    setCouponCode,
    clearCouponCode,
  } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [removingItems, setRemovingItems] = useState(new Set());

  // Remove the redirect for guest users - they can now use the cart
  // useEffect(() => {
  //   if (!user) {
  //     navigate("/login");
  //   }
  // }, [user, navigate]);

  const handleQuantityUpdate = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    setUpdatingItems((prev) => new Set(prev).add(itemId));
    try {
      await updateCartItem(itemId, newQuantity);
      // Only show toast for significant changes to avoid spam
      if (
        Math.abs(
          newQuantity -
            cart.items.find((item) => item.id === itemId)?.quantity || 0,
        ) > 1
      ) {
        toast.success("Cart updated");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update cart");
    } finally {
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleRemoveItem = async (itemId) => {
    const item = cart.items.find((item) => item.id === itemId);
    if (!item) return;

    setRemovingItems((prev) => new Set(prev).add(itemId));
    try {
      await removeFromCart(itemId);
      toast.success(`${item.variant.product.name} removed from cart`);
    } catch (error) {
      toast.error(error.message || "Failed to remove item");
    } finally {
      setRemovingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleClearCart = async () => {
    const itemCount = cart.items.length;
    if (
      !window.confirm(
        `Are you sure you want to remove all ${itemCount} items from your cart?`,
      )
    ) {
      return;
    }

    try {
      await clearCart();
      toast.success(`Cart cleared - ${itemCount} items removed`);
    } catch (error) {
      toast.error(error.message || "Failed to clear cart");
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return "/api/placeholder/200/200";
    if (imageUrl.startsWith("http")) return imageUrl;
    if (imageUrl.startsWith("/api/placeholder")) return imageUrl;
    const base = (API_BASE_URL || "").replace(/\/+$/, "");
    let cleanUrl = imageUrl
      .replace(/^\/uploads\//, "")
      .replace(/^uploads\//, "");
    return `${base}/uploads/${cleanUrl}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  if (loading && (!cart?.items || cart.items.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LightLoader size="lg" />
      </div>
    );
  }

  const hasOutOfStockItems =
    cart?.items?.some(
      (item) =>
        item.quantity > (item.variant?.stock ?? 0) ||
        (item.variant?.stock ?? 0) <= 0,
    ) ?? false;

  if (!cartError && (!cart || !cart.items || cart.items.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/shop"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Continue Shopping
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
          </div>

          {/* Empty Cart */}
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <ShoppingBag className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">
              Your cart is empty
            </h2>
            <p className="text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
              Looks like you haven't added any items to your cart yet. Start
              shopping to fill it up!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Link
                to="/shop"
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-black text-white font-medium hover:bg-gray-800 transition-colors text-sm sm:text-base"
              >
                <Package className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Start Shopping
              </Link>
              <Link
                to="/"
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <ArrowLeft className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <Link
            to="/shop"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Continue Shopping
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Shopping Cart ({cart.summary.total_items} items)
              </h1>
              {!user && (
                <div className="flex items-center mt-2 text-sm text-primary-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Guest User - Your cart is saved locally
                </div>
              )}
              {cart.items.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  Review your items and proceed to checkout
                </p>
              )}
            </div>
            {cart.items.length > 0 && (
              <button
                onClick={handleClearCart}
                className="text-red-600 hover:text-red-700 font-medium text-sm self-start sm:self-auto flex items-center"
                title="Remove all items from cart"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear Cart ({cart.items.length})
              </button>
            )}
          </div>
        </div>

        {cartError && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{cartError}</p>
            </div>
            <button
              type="button"
              onClick={() => refetchCart()}
              className="text-sm font-medium underline hover:no-underline self-start sm:self-auto"
            >
              Retry
            </button>
          </div>
        )}

        {hasOutOfStockItems && (
          <div className="mb-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              Some items are out of stock or exceed available quantity. Remove or
              update them before checkout.
            </p>
          </div>
        )}

        {/* Mobile Cart Summary - Fixed at bottom */}
        <div className="lg:hidden mb-6">
          <CartSummary
            cart={cart}
            formatPrice={formatPrice}
            isMobile={true}
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            clearCouponCode={clearCouponCode}
            hasOutOfStockItems={hasOutOfStockItems}
          />
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                onQuantityUpdate={handleQuantityUpdate}
                onRemove={handleRemoveItem}
                isUpdating={updatingItems.has(item.id)}
                isRemoving={removingItems.has(item.id)}
                getImageUrl={getImageUrl}
                formatPrice={formatPrice}
              />
            ))}
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <CartSummary
              cart={cart}
              formatPrice={formatPrice}
              isMobile={false}
              couponCode={couponCode}
              setCouponCode={setCouponCode}
              clearCouponCode={clearCouponCode}
              hasOutOfStockItems={hasOutOfStockItems}
            />
          </div>
        </div>

        {/* Mobile Cart Items */}
        <div className="lg:hidden space-y-4 pb-6">
          {cart.items.map((item) => (
            <CartItem
              key={item.id}
              item={item}
              onQuantityUpdate={handleQuantityUpdate}
              onRemove={handleRemoveItem}
              isUpdating={updatingItems.has(item.id)}
              isRemoving={removingItems.has(item.id)}
              getImageUrl={getImageUrl}
              formatPrice={formatPrice}
              isMobile={true}
            />
          ))}
        </div>

        {/* Add Clear Cart button at the bottom if there are items */}
        {cart.items.length > 0 && (
          <div className="fixed bottom-20 left-0 right-0 flex justify-center z-40 lg:static lg:justify-end lg:mt-8">
            <button
              onClick={handleClearCart}
              className="bg-red-600 text-white font-bold py-3 px-6 sm:px-8 rounded-lg shadow-lg hover:bg-red-700 transition-all duration-200 flex items-center"
              title="Remove all items from cart"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Cart ({cart.items.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Cart Item Component
const CartItem = ({
  item,
  onQuantityUpdate,
  onRemove,
  isUpdating,
  isRemoving,
  getImageUrl,
  formatPrice,
  isMobile,
}) => {
  const variant = item.variant;
  if (!variant?.product) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-red-200">
        <p className="text-sm text-red-600">
          This cart item is no longer available. Please remove it to continue.
        </p>
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="mt-3 text-sm font-medium text-red-700 underline"
        >
          Remove item
        </button>
      </div>
    );
  }

  const product = variant.product;
  const primaryImage =
    variant.images?.find((img) => img.is_primary) || variant.images?.[0];

  const currentPrice = variant.price_sale || variant.price_regular;
  const originalPrice = variant.price_regular;
  const hasDiscount =
    variant.price_sale && variant.price_sale < variant.price_regular;
  const discountPercentage = hasDiscount
    ? Math.round((1 - variant.price_sale / variant.price_regular) * 100)
    : 0;

  const itemTotal = currentPrice * item.quantity;
  const inStock = variant.stock > 0;
  const lowStock = variant.stock <= 5 && variant.stock > 0;

  return (
    <div
      className={`bg-white rounded-lg shadow-sm p-4 sm:p-6 transition-opacity relative ${
        isRemoving ? "opacity-50" : ""
      }`}
    >
      <div
        className={`${isMobile ? "space-y-4" : "flex items-start space-x-4"}`}
      >
        {/* Product Image */}
        <div
          className={`${isMobile ? "flex justify-center" : "flex-shrink-0"}`}
        >
          <Link to={`/products/${product.slug}`}>
            <ImageWithFallback
              src={getImageUrl(primaryImage?.url)}
              alt={primaryImage?.alt_text || product.name}
              className={`${
                isMobile ? "w-24 h-24" : "w-20 h-20"
              } object-cover rounded-lg hover:opacity-80 transition-opacity`}
              fallbackSrc="/api/placeholder/200/200"
            />
          </Link>
        </div>

        <div className={`${isMobile ? "space-y-4" : "flex-1 min-w-0"}`}>
          {/* Product Info */}
          <div
            className={`${
              isMobile ? "text-center" : "flex items-start justify-between"
            }`}
          >
            <div className={`${isMobile ? "space-y-2" : "min-w-0 flex-1"}`}>
              <Link
                to={`/products/${product.slug}`}
                className="text-base sm:text-lg font-semibold text-gray-900 hover:text-primary-600 line-clamp-2"
              >
                {product.name}
              </Link>

              {product.brand && (
                <p className="text-sm text-gray-600 mt-1">
                  Brand: {product.brand}
                </p>
              )}

              {/* Variant Attributes */}
              {variant.attributes && variant.attributes.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                  {variant.attributes.map((attr, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-xs font-medium text-gray-700"
                    >
                      {attr.name}: {attr.value}
                    </span>
                  ))}
                </div>
              )}

              {/* Stock Status */}
              <div className="flex items-center space-x-2 mt-2 justify-center sm:justify-start">
                {!inStock ? (
                  <span className="inline-flex items-center text-red-600 text-sm">
                    <AlertCircle className="mr-1 h-4 w-4" />
                    Out of Stock
                  </span>
                ) : lowStock ? (
                  <span className="inline-flex items-center text-orange-600 text-sm">
                    <AlertCircle className="mr-1 h-4 w-4" />
                    Only {variant.stock} left
                  </span>
                ) : (
                  <span className="text-green-600 text-sm">
                    In Stock ({variant.stock} available)
                  </span>
                )}
              </div>
            </div>

            {/* Remove Button */}
            <button
              onClick={() => onRemove(item.id)}
              disabled={isRemoving}
              className={`${
                isMobile ? "absolute top-2 right-2" : "ml-4"
              } p-2 text-primary-400 hover:text-primary-600 transition-colors disabled:opacity-50 hover:bg-primary-50 rounded-full`}
              title={`Remove ${product.name} from cart`}
            >
              {isRemoving ? (
                <LightLoader size="sm" />
              ) : (
                <Trash2 className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Price and Quantity */}
          <div
            className={`${
              isMobile ? "space-y-4" : "flex items-center justify-between mt-4"
            }`}
          >
            {/* Price */}
            <div
              className={`flex items-center space-x-2 ${
                isMobile ? "justify-center" : ""
              }`}
            >
              <span className="text-lg font-bold text-gray-900">
                {formatPrice(currentPrice)}
              </span>
              {hasDiscount && (
                <>
                  <span className="text-sm text-gray-500 line-through">
                    {formatPrice(originalPrice)}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-md bg-red-100 text-red-800 text-xs font-medium">
                    -{discountPercentage}%
                  </span>
                </>
              )}
            </div>

            {/* Quantity Controls */}
            <div
              className={`${
                isMobile
                  ? "flex flex-col items-center space-y-3"
                  : "flex items-center space-x-3"
              }`}
            >
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => onQuantityUpdate(item.id, item.quantity - 1)}
                  disabled={item.quantity <= 1 || isUpdating || !inStock}
                  className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[2.5rem]"
                  title={
                    item.quantity <= 1
                      ? "Minimum quantity reached"
                      : "Decrease quantity"
                  }
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-4 py-2 font-medium min-w-[3rem] text-center bg-white">
                  {isUpdating ? <LightLoader size="sm" /> : item.quantity}
                </span>
                <button
                  onClick={() => onQuantityUpdate(item.id, item.quantity + 1)}
                  disabled={
                    item.quantity >= variant.stock || isUpdating || !inStock
                  }
                  className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[2.5rem]"
                  title={
                    item.quantity >= variant.stock
                      ? "Maximum stock reached"
                      : "Increase quantity"
                  }
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <div className={`text-right ${isMobile ? "text-center" : ""}`}>
                <div className="text-lg font-bold text-gray-900">
                  {formatPrice(itemTotal)}
                </div>
                <div className="text-xs text-gray-500">
                  {item.quantity} × {formatPrice(currentPrice)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cart Summary Component
const CartSummary = ({
  cart,
  formatPrice,
  isMobile,
  couponCode,
  setCouponCode,
  clearCouponCode,
  hasOutOfStockItems = false,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [couponInput, setCouponInput] = useState(couponCode || "");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponPreview, setCouponPreview] = useState(null);
  const [couponError, setCouponError] = useState("");

  const orderItems = useMemo(
    () =>
      cart.items.map((item) => ({
        variant_id: item.variant_id || item.variant?.id,
        quantity: item.quantity,
      })),
    [cart.items],
  );

  const deliveryCharge = useMemo(() => {
    const subtotal = cart.summary.subtotal;
    return subtotal >= 2500 ? 0 : 80;
  }, [cart.summary.subtotal]);

  const discountAmount = couponPreview?.discountAmount || 0;
  const itemsAfterDiscount =
    couponPreview?.itemsAfterDiscount ?? cart.summary.subtotal;
  const totalWithDelivery = itemsAfterDiscount + deliveryCharge;

  const runCouponValidation = useCallback(
    async (code) => {
      if (!code?.trim()) {
        setCouponPreview(null);
        setCouponError("");
        return null;
      }
      setCouponLoading(true);
      setCouponError("");
      try {
        const res = await validateCoupon(code, orderItems);
        if (res?.valid) {
          setCouponPreview(res);
          setCouponCode(code.trim().toUpperCase());
          return res;
        }
        setCouponPreview(null);
        setCouponError(res?.message || "Invalid coupon");
        return null;
      } catch (err) {
        setCouponPreview(null);
        setCouponError(err.message || "Could not validate coupon");
        return null;
      } finally {
        setCouponLoading(false);
      }
    },
    [orderItems, setCouponCode],
  );

  useEffect(() => {
    if (couponCode && cart.items.length > 0) {
      runCouponValidation(couponCode);
    } else if (!couponCode) {
      setCouponPreview(null);
      setCouponError("");
    }
  }, [couponCode, cart.items.length, runCouponValidation]);

  const handleApplyCoupon = async () => {
    const result = await runCouponValidation(couponInput);
    if (result) toast.success("Coupon applied");
  };

  const handleRemoveCoupon = () => {
    setCouponInput("");
    clearCouponCode();
    setCouponPreview(null);
    setCouponError("");
    toast.success("Coupon removed");
  };

  const handleCheckout = () => {
    if (couponCode && couponError) {
      toast.error("Remove or fix the invalid coupon before checkout");
      return;
    }
    if (couponCode && !couponPreview?.valid) {
      toast.error("Please wait for coupon validation");
      return;
    }
    navigate("/checkout");
  };

  const checkoutDisabled =
    cart.items.length === 0 ||
    hasOutOfStockItems ||
    (couponCode && (!couponPreview?.valid || couponLoading));

  return (
    <div
      className={`bg-white rounded-lg shadow-sm ${
        isMobile ? "p-4" : "p-6 sticky top-8"
      }`}
    >
      <h2
        className={`font-semibold text-gray-900 mb-4 ${
          isMobile ? "text-lg" : "text-xl"
        }`}
      >
        Order Summary
      </h2>

      {/* Coupon */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Coupon code
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
            placeholder="Enter code"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase"
            disabled={couponLoading}
          />
          {couponCode && couponPreview?.valid ? (
            <button
              type="button"
              onClick={handleRemoveCoupon}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Remove
            </button>
          ) : (
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={couponLoading || !couponInput.trim()}
              className="px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {couponLoading ? "..." : "Apply"}
            </button>
          )}
        </div>
        {couponError && (
          <p className="text-xs text-red-600 mt-1">{couponError}</p>
        )}
        {couponPreview?.valid && (
          <p className="text-xs text-green-600 mt-1">
            {couponPreview.message || "Coupon applied"}
          </p>
        )}
      </div>

      {/* Summary Details */}
      <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
        <div className="flex justify-between text-gray-600 text-sm sm:text-base">
          <span>Subtotal ({cart.summary.total_items} items)</span>
          <span>{formatPrice(cart.summary.subtotal)}</span>
        </div>

        {discountAmount > 0 && (
          <div className="flex justify-between text-green-600 text-sm sm:text-base">
            <span>Discount ({couponCode})</span>
            <span>-{formatPrice(discountAmount)}</span>
          </div>
        )}

        <div className="flex justify-between text-gray-600 text-sm sm:text-base">
          <span>Delivery Charges</span>
          <span
            className={
              deliveryCharge === 0
                ? "text-green-600 font-medium"
                : "text-gray-900 font-medium"
            }
          >
            {deliveryCharge === 0 ? "Free" : `₹${deliveryCharge}`}
          </span>
        </div>
        {deliveryCharge > 0 && (
          <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
            💡 Add ₹{(2500 - cart.summary.subtotal).toFixed(2)} more for free
            delivery
          </div>
        )}

        <div className="border-t pt-3 sm:pt-4">
          <div className="flex justify-between font-bold text-gray-900 text-lg sm:text-xl">
            <span>Total</span>
            <span>{formatPrice(totalWithDelivery)}</span>
          </div>
          <div className="text-center mt-2">
            <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
              Inclusive of all taxes
            </span>
          </div>
        </div>
      </div>

      {/* Guest User Notice */}
      {!user && (
        <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-4 h-4 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm text-primary-800">
              <p className="font-medium mb-1">Guest Checkout Available</p>
              <p>
                No account needed — enter your details at checkout and use the
                tracking link we email you to follow your order anytime.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cart Summary Info */}
      <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Items in cart:</span>
          <span className="font-medium text-gray-900">
            {cart.summary.total_items}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-gray-600">Estimated delivery:</span>
          <span className="font-medium text-gray-900">3-5 business days</span>
        </div>
      </div>

      {/* Checkout Button */}
      <button
        onClick={handleCheckout}
        disabled={checkoutDisabled}
        className="w-full bg-black text-white py-3 sm:py-4 px-4 sm:px-6 font-semibold text-base sm:text-lg hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 mb-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        title="Complete your purchase"
      >
        <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
        <span>Proceed to Checkout</span>
      </button>

      {/* Trust Badges - Hidden on mobile for space */}
      {!isMobile && (
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center text-sm text-gray-600">
            <Shield className="h-4 w-4 mr-2 text-green-600" />
            <span>Secure checkout</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Truck className="h-4 w-4 mr-2 text-blue-600" />
            <span>Free delivery on orders above ₹2500</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Package className="h-4 w-4 mr-2 text-purple-600" />
            <span>Easy returns within 30 days</span>
          </div>
        </div>
      )}

      {/* Continue Shopping */}
      <Link
        to="/shop"
        className="block w-full text-center mt-4 py-2 text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
      >
        Continue Shopping
      </Link>
    </div>
  );
};

export default CartPage;
